import posthog from 'posthog-js';

import { SingSetup, Song, SongPreview } from '~/interfaces';
import { PlayerNumber } from '~/modules/players/player-number';
import { PlayerEntity, SelectedPlayerInput } from '~/modules/players/players-manager';
import { transportErrorReason } from '~/modules/remote-mic/network/client/network-client';
import { keyStrokes } from '~/modules/remote-mic/network/messages';
import { SongStats } from '~/modules/songs/stats/common';
import { RemoteMicPermission } from '~/routes/settings/settings-state';

export class GameEvent<T extends (...args: any[]) => void> {
  protected subscribers: Array<T> = [];
  public lastParams: Parameters<T> | null = null;

  public subscribe = (callback: T, getLast = false) => {
    this.unsubscribe(callback);
    this.subscribers.push(callback);
    if (getLast && this.lastParams !== null) {
      callback(...this.lastParams);
    }
  };

  public unsubscribe = (callback: T) => {
    this.subscribers = this.subscribers.filter((cb) => cb !== callback);
  };

  public dispatch = (...args: Parameters<T>) => {
    this.log && console.log('dispatch', this.name, ...args);
    this.subscribers.forEach((callback) => callback(...args));
    this.lastParams = args;

    if (this.track) {
      posthog.capture(this.name, this.track instanceof Function ? this.track(...args) : args);
    }
  };

  public constructor(
    private name: string,
    private track: boolean | ((...args: Parameters<T>) => any) = false,
    private log = true,
  ) {}
}

export const events = {
  sectionChange: new GameEvent<(player: PlayerNumber, previousSectionIndex: number) => void>(
    'sectionChange',
    false,
    false,
  ),
  // newPlayerNote: new GameEvent<(player: 0 | 1 |2 | 3, playerNote: PlayerNote) => void>('//', true),
  // playerNoteUpdate: new GameEvent<(player: 0 | 1 |2 | 3, playerNote: PlayerNote) => void>('//', true),

  playerAdded: new GameEvent<(playerNumber: PlayerNumber) => void>('playerAdded', false),
  playerRemoved: new GameEvent<(player: PlayerEntity) => void>('playerRemoved', false),

  songStarted: new GameEvent<(song: Song | SongPreview, singSetup: SingSetup) => void>('songStarted', false),
  songEnded: new GameEvent<
    (
      song: Song | SongPreview,
      singSetup: SingSetup,
      scores: Array<{ name: string; score: number }>,
      progress: number,
    ) => void
  >('songEnded', false),

  remoteMicConnected: new GameEvent<(remoteMic: { id: string; name: string; silent: boolean }) => void>(
    'remoteMicConnected',
  ),
  remoteMicDisconnected: new GameEvent<(remoteMic: { id: string; name: string }, silent: boolean) => void>(
    'remoteMicDisconnected',
  ),
  remoteMicRenamed: new GameEvent<(remoteMic: { id: string; name: string }) => void>('remoteMicRenamed'),
  playerNameChanged: new GameEvent<(playerNumber: PlayerNumber, oldName: string | undefined) => void>(
    'playerNameChanged',
  ),
  playerNumberChanged: new GameEvent<(playerNumber: PlayerNumber, previousNumber: PlayerNumber) => void>(
    'playerNumberChanged',
  ),
  playerInputChanged: new GameEvent<
    (
      playerNumber: PlayerNumber,
      oldInput: SelectedPlayerInput | undefined,
      newInput: SelectedPlayerInput | undefined,
    ) => void
  >('playerInputChanged'),
  inputListChanged: new GameEvent<(initial: boolean) => void>('inputListChanged'),

  karaokeConnectionStatusChange: new GameEvent<
    (
      status: 'connecting' | 'connected' | 'disconnected' | 'reconnecting' | 'error' | 'uninitialised',
      e?: transportErrorReason,
    ) => void
  >('karaokeConnectionStatusChange'),
  remoteMicPlayerSet: new GameEvent<(playerNumber: PlayerNumber | null) => void>('remoteMicPlayerSet'),
  remoteMicMonitoringStarted: new GameEvent('remoteMicMonitoringStarted'),
  remoteMicMonitoringStopped: new GameEvent('remoteMicMonitoringStopped'),

  micMonitoringStarted: new GameEvent('micMonitoringStarted'),
  micMonitoringStopped: new GameEvent('micMonitoringStopped'),
  playerChangeRequested: new GameEvent<(remoteMicId: string, newPlayerNumber: PlayerNumber | null) => void>(
    'playerChangeRequested',
  ),

  songStatStored: new GameEvent<(key: string, stats: SongStats) => void>('songStatStored'),
  songScoreUpdated: new GameEvent<(key: string, stats: SongStats, newName: string) => void>('songScoreUpdated'),

  remoteKeyboardPressed: new GameEvent<(key: keyStrokes) => void>('remoteKeyboardPressed'),
  // A mirrored control was tapped directly on the remote mic; `name` matches a useKeyboardNav register() name.
  remoteControlActivated: new GameEvent<(name: string) => void>('remoteControlActivated'),
  // A mirrored value control (e.g. a text field) was edited on the remote mic; `name` matches a
  // useKeyboardNav register() name and `value` is the new text the host should apply.
  remoteControlValueChanged: new GameEvent<(name: string, value: string) => void>('remoteControlValueChanged'),
  remoteSongSearch: new GameEvent<(search: string) => void>('remoteSongSearch'),
  // A playlist was picked on the remote mic's mirrored song-selection toolbar; `name` matches a
  // PlaylistEntry name.
  remotePlaylistSelected: new GameEvent<(name: string) => void>('remotePlaylistSelected'),
  // A song group was tapped on the remote mic's mirrored group nav row; `name` matches a SongGroup name.
  remoteSongGroupSelected: new GameEvent<(name: string) => void>('remoteSongGroupSelected'),
  remoteSongSelected: new GameEvent<(search: string) => void>('remoteSongSelected', true),
  remoteMicSongListUpdated: new GameEvent<(id: string, delta: { added?: string[]; deleted?: string[] }) => void>(
    'remoteMicSongListUpdate',
  ),
  remoteReadinessRequested: new GameEvent('remoteReadinessRequested'),
  remoteMicPermissionsSet: new GameEvent<(level: RemoteMicPermission) => void>('remoteMicPermissionsSet'),
  readinessConfirmed: new GameEvent<(deviceId: string) => void>('remoteReadinessConfirmed'),
  minPlayerNumberChanged: new GameEvent<(previous: number, current: number) => void>('minPlayerNumberChanged'),

  micServerStarted: new GameEvent('micServerStarted'),
  micServerStopped: new GameEvent('micServerStopped'),

  noteStarted: new GameEvent('noteStarted', false, false),
};

export default events;
